import { Badge } from '@workspace/ui/components/badge'
import { cn } from '@workspace/ui/lib/utils'
import { Mail, ThumbsDown } from 'lucide-react'
import {
  useAdminInvestorDealFlowInteractionsQuery,
  useAdminStartupDealFlowInteractionsQuery,
} from '../../../hooks/api'

type InteractionRow = {
  id: string
  kind: 'PASS' | 'CONNECT'
  reasons: string[]
  note: string | null
  message: string | null
  createdAt: string | Date
  investorId: string
  investorName: string
  startupId: string
  startupName: string
}

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function KindBadge({ kind }: { kind: 'PASS' | 'CONNECT' }) {
  if (kind === 'CONNECT') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-300">
        <Mail className="size-3" />
        Connect
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-300">
      <ThumbsDown className="size-3" />
      Pass
    </span>
  )
}

function InteractionList({
  items,
  isLoading,
  emptyMessage,
  showInvestor = false,
  showStartup = false,
}: {
  items: InteractionRow[]
  isLoading: boolean
  emptyMessage: string
  showInvestor?: boolean
  showStartup?: boolean
}) {
  if (isLoading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border/60 bg-card/30 px-6 py-12 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {items.map((row) => (
        <div
          key={row.id}
          className={cn(
            'rounded-lg border bg-card/40 px-4 py-3',
            row.kind === 'CONNECT' ? 'border-emerald-500/20' : 'border-red-500/20'
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            <KindBadge kind={row.kind} />
            {showStartup && (
              <span className="text-sm font-semibold text-foreground">{row.startupName}</span>
            )}
            {showInvestor && (
              <span className="text-sm font-semibold text-foreground">{row.investorName}</span>
            )}
            <span className="ml-auto text-[11px] text-muted-foreground">
              {formatDate(row.createdAt)}
            </span>
          </div>

          {row.kind === 'PASS' && row.reasons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {row.reasons.map((reason) => (
                <Badge key={reason} variant="secondary" className="text-[11px]">
                  {reason}
                </Badge>
              ))}
            </div>
          )}

          {row.kind === 'PASS' && row.note && (
            <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
              {row.note}
            </p>
          )}

          {row.kind === 'CONNECT' && row.message && (
            <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">
              {row.message}
            </p>
          )}
        </div>
      ))}
    </div>
  )
}

export function InvestorDealFlowInteractionsTab({ investorId }: { investorId: string }) {
  const query = useAdminInvestorDealFlowInteractionsQuery(investorId)
  const items = (query.data?.data ?? []) as InteractionRow[]
  return (
    <InteractionList
      items={items}
      isLoading={query.isLoading}
      emptyMessage="This investor hasn't passed or connected with any startups yet."
      showStartup
    />
  )
}

export function StartupDealFlowInteractionsTab({ startupId }: { startupId: string }) {
  const query = useAdminStartupDealFlowInteractionsQuery(startupId)
  const items = (query.data?.data ?? []) as InteractionRow[]
  return (
    <InteractionList
      items={items}
      isLoading={query.isLoading}
      emptyMessage="No investors have passed or connected with this startup yet."
      showInvestor
    />
  )
}
